import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Injector,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { takeUntil } from 'rxjs';
import { TuiDialogService } from '@taiga-ui/core';
import { PolymorpheusComponent } from '@taiga-ui/polymorpheus';
import { TranslateService } from '@ngx-translate/core';
import { BaseResizableComponent } from '@src/app/modules/base';
import { OrganisationService, UserService } from '@src/core/services';
import { CustomNamesService } from '@src/app/modules/custom-name-tabs';
import { EnvService } from '@src/app/modules/env';
import { LogoutService } from '@src/app/modules/auth';
import { DialogConfirmComponent } from '@src/app/shared/dialogs';
import { Optional } from '@src/types/utils';
import { InternalMessengerService } from '@src/app/modules/internal-messenger';

import { NavigationItem, NavigationMode, NavigationTree } from '../types';
import { NAVIGATION_ITEMS } from '../constants';

@Component({
  selector: 'app-navigation-list',
  templateUrl: './navigation-list.component.html',
  styleUrls: ['./navigation-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NavigationListComponent extends BaseResizableComponent implements OnChanges {
  private readonly organisationService = inject(OrganisationService);
  private readonly userService = inject(UserService);
  private readonly customNamesService = inject(CustomNamesService);
  private readonly logoutService = inject(LogoutService);
  private readonly env = inject(EnvService);
  private readonly translateService = inject(TranslateService);
  private readonly router = inject(Router);
  private readonly dialogService = inject(TuiDialogService);
  private readonly injector = inject(Injector);
  private readonly internalMessengerService = inject(InternalMessengerService);

  @Input() isOpen?: boolean = true;
  @Input() descriptionVisible: boolean = false;
  @Input() additionalNavItems?: NavigationItem[] | null;
  @Input() navTree: NavigationTree[] = [];
  @Input() mode?: NavigationMode = 'desktop';
  @Input() selectedItem?: NavigationItem;
  @Output() selectedItemChange = new EventEmitter<Optional<NavigationItem>>();

  items?: NavigationItem[];

  readonly parentOrganisations$ = this.organisationService.parentOrganisations$;
  readonly authUser$ = this.userService.authUser$;
  readonly allCustomNames$ = this.customNamesService.allCustomNames$;

  private readonly confirmDialog = this.dialogService.open<boolean>(
    new PolymorpheusComponent(DialogConfirmComponent, this.injector),
    {
      label: this.translateService.instant('common.dialogs.logOutHeader'),
      size: 's',
      closeable: false,
    },
  );

  constructor() {
    super();

    this.router.events.pipe(takeUntil(this.destroyed$$)).subscribe(routerEvent => {
      if (routerEvent instanceof NavigationEnd) {
        this.setSelectedItem();
        this.cdr.markForCheck();
      }
    });

    this.parentOrganisations$.pipe(takeUntil(this.destroyed$$)).subscribe(() => {
      this.customNamesService.getData();
    });
  }

  protected afterChangeScreenType() {
    this.initNavigation();
  }

  override ngOnInit(): void {
    super.ngOnInit();

    this.allCustomNames$.pipe(takeUntil(this.destroyed$$)).subscribe(allCustomNames => {
      if (!allCustomNames?.length) return;

      this.initNavigation();
    });

    this.cdr.markForCheck();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.navTree || changes['additionalNavItems']) {
      this.initNavigation();
    }

    this.cdr.markForCheck();
  }

  private initNavigation(): void {
    const navItems: NavigationItem[] = NAVIGATION_ITEMS.map(item => Object.assign({}, item)); // Копирование без ссылки на объект

    this.items = navItems
      .filter(navItem => !!this.navTree.find(navTreeItem => navTreeItem.itemName === navItem.id))
      .filter(navItem => {
        if (navItem.id === 'menu.folders' && this.env.isBot) {
          return false;
        }

        return true;
      })
      .map(navItem => {
        const treeItem = this.navTree.find(navTreeItem => navTreeItem.itemName === navItem.id);

        navItem.title = treeItem?.title ?? this.customNamesService.getItem(navItem.id)?.title ?? navItem.title;
        navItem.link = treeItem?.link ?? navItem.link;
        navItem.queryParams = treeItem?.queryParams ?? navItem.queryParams;
        navItem.permissions = treeItem?.permissions ?? navItem.permissions;

        if (navItem.id === 'menu.curator') {
          navItem.action = () => this.onClickCuratorButton();
        }

        if (navItem.id === 'menu.logout') {
          navItem.action = () => this.onClickExitButton();
        }

        if (navItem.id === 'menu.chatsInner') {
          navItem.badgeCount = this.internalMessengerService.unreadChatsCount$;
        }

        if (navItem.id === 'menu.chatPublic') {
          navItem.badgeCount = this.internalMessengerService.publicChatUnreadMessageCount$;
        }

        return navItem;
      })
      .sort((navItem1, navItem2) => {
        const treeItemIndex1 = this.navTree.findIndex(navTreeItem => navTreeItem.itemName === navItem1.id);
        const treeItemIndex2 = this.navTree.findIndex(navTreeItem => navTreeItem.itemName === navItem2.id);

        return treeItemIndex1 - treeItemIndex2;
      });

    if (this.additionalNavItems) {
      const additionalNavPosition = this.items.findIndex(navItem => navItem.id === 'menu.settings');
      this.items.splice(additionalNavPosition, 0, ...this.additionalNavItems);
    }

    this.setSelectedItem();

    this.cdr.markForCheck();
  }

  private onClickCuratorButton(): void {
    this.userService.communicationWithCurator();
  }

  private onClickExitButton(): void {
    this.confirmDialog.pipe(takeUntil(this.destroyed$$)).subscribe({
      next: async res => {
        if (res) {
          await this.logoutService.logout();
        }
      },
    });
  }

  private setSelectedItem() {
    this.selectedItem = this.items?.find(item => item.link && this.router.url?.includes(item.link));
    this.selectedItemChange.emit(this.selectedItem);
  }
}
